import { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import type { TableProps } from 'antd';
import type { DatePreset } from '@/models/report';
import type {
  UploadListParams,
  UploadStatus,
  UploadRow,
  UploadStudyType,
} from '@/models/upload';

function resolveStudyDateRange(
  preset: DatePreset,
  custom: [string, string] | null
): { study_date_from?: string; study_date_to?: string } {
  const today = moment().format('YYYY-MM-DD');
  switch (preset) {
    case 'today':
      return { study_date_from: today, study_date_to: today };
    case 'last7':
      return {
        study_date_from: moment().subtract(7, 'days').format('YYYY-MM-DD'),
        study_date_to: today,
      };
    case 'last30':
      return {
        study_date_from: moment().subtract(30, 'days').format('YYYY-MM-DD'),
        study_date_to: today,
      };
    case 'custom':
      if (!custom) return {};
      return { study_date_from: custom[0], study_date_to: custom[1] };
    default:
      return {};
  }
}

export function useUploadsListFilters() {
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(10);

  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [selectedStatuses, setSelectedStatuses] = useState<UploadStatus[]>([]);
  const [selectedStudyTypes, setSelectedStudyTypes] = useState<
    UploadStudyType[]
  >([]);
  const [selectedSites, setSelectedSites] = useState<string[]>([]);
  const [datePreset, setDatePreset] = useState<DatePreset | 'all'>('all');
  const [customDateRange, setCustomDateRange] = useState<
    [string, string] | null
  >(null);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(searchText.trim()), 350);
    return () => clearTimeout(id);
  }, [searchText]);

  const hasActiveFilters = useMemo(
    () =>
      selectedStatuses.length > 0 ||
      selectedStudyTypes.length > 0 ||
      selectedSites.length > 0 ||
      datePreset !== 'all',
    [selectedStatuses, selectedStudyTypes, selectedSites, datePreset]
  );

  const hasActiveSearch = debouncedSearch.length > 0;

  const params = useMemo<UploadListParams>(() => {
    const dateRange =
      datePreset === 'all'
        ? {}
        : resolveStudyDateRange(datePreset, customDateRange);
    return {
      page,
      size,
      search: debouncedSearch || undefined,
      status: selectedStatuses.length > 0 ? selectedStatuses : undefined,
      modality: selectedStudyTypes.length > 0 ? selectedStudyTypes : undefined,
      site: selectedSites.length > 0 ? selectedSites : undefined,
      ...dateRange,
    };
  }, [
    page,
    size,
    debouncedSearch,
    selectedStatuses,
    selectedStudyTypes,
    selectedSites,
    datePreset,
    customDateRange,
  ]);

  const onTableChange: TableProps<UploadRow>['onChange'] = (pagination) => {
    setPage(pagination?.current ?? page);
    setSize(pagination?.pageSize ?? size);
  };

  const clearFilters = () => {
    setSelectedStatuses([]);
    setSelectedStudyTypes([]);
    setSelectedSites([]);
    setDatePreset('all');
    setCustomDateRange(null);
    setPage(1);
  };

  const clearSearch = () => {
    setSearchText('');
    setDebouncedSearch('');
    setPage(1);
  };

  return {
    params,
    page,
    size,
    searchText,
    setSearchText,
    selectedStatuses,
    onStatusesChange: (statuses: UploadStatus[]) => {
      setSelectedStatuses(statuses);
      setPage(1);
    },
    selectedStudyTypes,
    onStudyTypesChange: (types: UploadStudyType[]) => {
      setSelectedStudyTypes(types);
      setPage(1);
    },
    selectedSites,
    onSitesChange: (sites: string[]) => {
      setSelectedSites(sites);
      setPage(1);
    },
    datePreset,
    onDatePresetChange: (preset: DatePreset | 'all') => {
      setDatePreset(preset);
      if (preset !== 'custom') setCustomDateRange(null);
      setPage(1);
    },
    customDateRange,
    onCustomDateRangeChange: setCustomDateRange,
    hasActiveFilters,
    hasActiveSearch,
    clearFilters,
    clearSearch,
    onTableChange,
  };
}
